// Customer-level theming, all driven by TB customer SERVER_SCOPE attributes
// that arrive on `branding` via /api/ref/validate:
//
//   brandColor      primary color → nav-header background      (e.g. '#000000')
//   navHeaderTheme  'dark' | 'light' → text + myHuB logo contrast
//   font            CSS font-family → whole app                (e.g. 'sans-serif')
//
// Example (Blackstone):
//   brandColor=#000000, navHeaderTheme=dark, font=sans-serif
//   → black header, white text + white myHuB logo, sans-serif everywhere.
//
// navHeaderTheme is explicit; if absent we fall back to deriving contrast from
// the primary color's luminance so a customer can re-skin with brandColor alone.

// Perceived luminance check — true when white foreground reads better than dark.
export function isDarkColor(hex) {
  if (!hex || typeof hex !== 'string') return false;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const L = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255; // sRGB, 0–1
  return L < 0.5;
}

// Returns { headerStyle, appStyle, dark, myhubLogo }:
//   headerStyle → spread onto the .hero element (background + contrast vars)
//   appStyle    → spread onto the app root (font var, applies app-wide)
//   myhubLogo   → which MyHub logo asset to render
export function getCustomerTheme(branding) {
  const primary = branding?.brandColor || null;

  const dark =
    branding?.navHeaderTheme === 'dark' ||
    (branding?.navHeaderTheme !== 'light' && isDarkColor(primary));

  const headerStyle = {
    ...(primary && { '--header-bg': primary }),
    ...(dark && { '--header-text': '#ffffff' }),
  };

  // Root-level vars so BOTH the header and the body can theme off them.
  const appStyle = {
    ...(primary && { '--brand-color': primary }),
    ...(branding?.font && { '--app-font': branding.font }),
    ...(branding?.headingFont && { '--heading-font': branding.headingFont }),
    // Client-logo width cap. Logos are sized by HEIGHT with width:auto, so a
    // wide banner (e.g. 483x100, ~4.8:1) renders far wider than a squarer mark
    // at the same height and crowds the title. Settable per customer OR per
    // floor via the logoMaxWidth attribute; unset leaves the default (none).
    ...(branding?.logoMaxWidth && { '--logo-max-width': branding.logoMaxWidth }),
  };

  const myhubLogo = dark ? './logos/myhub-on-dark.png' : './logos/myhub.png';
  return { headerStyle, appStyle, dark, myhubLogo };
}
